import React, { useState, useEffect, useMemo } from "react";
import { CircularProgress } from "@mui/material";
import Swal from "sweetalert2";

import {
  MutationWithArrayInput,
  fetchDataEngine,
  graphqlQuery,
} from "../../../data/Axios/DynamicService";

import {
  HRService,
  setupManagementUrl,
} from "../../../config";

import {
  AllEmployeesQuery,
  allLeaveTypes,
  allPoliciesView,
  companyCalendarSetups,
} from "../../../data/Queries/HRmanagement/leavemanagement";

import { allGenders } from "../../../data/Axios/queries";
import { leaveApplicationFields } from "../../../data/DynamicTable/FormsFields/LeaveApplicationForms";
import DynamicForm from "../../../data/Axios/DynamicForm";
import { uploadDocuments } from "../../../data/DocumentManager/documentManagerClient";

const base_url = HRService.uri;

const LeaveApplicationForm = (props) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userId = parseInt(decodedToken.Id);
  const rolegroupId = parseInt(decodedToken.RoleGroupId);
  const companyId = parseInt(decodedToken.CompanyId);
  const loggedInEmployeeId = parseInt(decodedToken.EmployeeId);

  const [leaveTypes, setLeaveTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [femaleGenderId, setFemaleGenderId] = useState(null);
  const [selectedEmployeeGenderId, setSelectedEmployeeGenderId] = useState(null);
  const [selectedLeaveTypeId, setSelectedLeaveTypeId] = useState(
    props.data ? props.data.leaveTypeId : ""
  );
  const [showEmployeeDropDown, setShowEmployeeDropDown] = useState(false);
  const [excludeFromRelievers, setExcludeFromRelievers] = useState(loggedInEmployeeId);
  const [loading, setLoading] = useState(true);
  const [existingRelievers, setExistingRelievers] = useState([]);
  const [holidays, setHolidays] = useState([]);

  // helper to convert whatever date string comes back from the API into
  // a yyyy‑mm‑dd value that the HTML date control expects.  leave it in a
  // function so the same logic can be reused later.
  // Convert a date value to a yyyy-mm-dd string WITHOUT timezone shifting.
  // new Date("2026-03-11").toISOString() returns UTC midnight which in timezones
  // east of UTC (e.g. UTC+3) produces "2026-03-10T21:00:00Z" → "2026-03-10".
  // We avoid that by reading the local date parts directly.
  const formatDate = (d) => {
    if (!d) return "";
    // If it already looks like a plain date string (yyyy-mm-dd or dd/mm/yyyy), extract it directly
    if (typeof d === "string") {
      // Match yyyy-mm-dd (possibly followed by T...)
      const isoMatch = d.match(/^(\d{4}-\d{2}-\d{2})/);
      if (isoMatch) return isoMatch[1];
      // Match dd/mm/yyyy
      const dmyMatch = d.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
      if (dmyMatch) return `${dmyMatch[3]}-${dmyMatch[2]}-${dmyMatch[1]}`;
    }
    // For actual Date objects or timestamps, use local time methods to avoid UTC shift
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return "";
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // Calculate end date by adding business days (excluding weekends and holidays).
  // Used for annual leave type.
  const calculateEndDateByBusinessDays = (startDateStr, businessDaysCount) => {
    const start = new Date(startDateStr);
    const holidayDates = new Set(
      holidays.map((h) => formatDate(h.holidayDate))
    );
    let currentDate = new Date(start);
    let daysAdded = 0;

    while (daysAdded < businessDaysCount) {
      currentDate.setDate(currentDate.getDate() + 1);
      const dayOfWeek = currentDate.getDay();
      const dateStr = formatDate(currentDate);

      // Skip weekends (0=Sunday, 6=Saturday) and holidays
      if (dayOfWeek !== 0 && dayOfWeek !== 6 && !holidayDates.has(dateStr)) {
        daysAdded++;
      }
    }

    return formatDate(currentDate);
  };

  // ---------- shared policy lookup helper ----------
  // Returns the best matching policy for a given leaveTypeId.
  // Prefers a gender-neutral policy; falls back to any policy for that type.
  // The gender-neutral check is intentionally permissive to cover all the ways
  // "no gender restriction" may be stored in the DB (null, 0, "", "-", "All").
  const findPolicy = (leaveTypeId) => {
    const id = parseInt(leaveTypeId, 10);
    if (isNaN(id) || policies.length === 0) return null;

    const isGenderNeutral = (g) =>
      g == null ||
      g === "" ||
      g === "-" ||
      g === "All" ||
      g === 0 ||
      g === "0" ||
      Number(g) <= 0;

    return (
      policies.find(
        (p) => parseInt(p.leaveTypeId, 10) === id && isGenderNeutral(p.gender)
      ) || policies.find((p) => parseInt(p.leaveTypeId, 10) === id)
    );
  };

  // build the object that Formik will feed into <DynamicForm>.  when the
  // component is reinitialised (editing an existing application) the
  // `enableReinitialize` prop will cause the form to reset.
  const initialValues = useMemo(() => {
    return {
      startDate: formatDate(props.data?.startDate) || "",
      endDate: formatDate(props.data?.endDate) || "",
      // frontend-only field – calculate from dates if we don't have stored value
      numberOfDays:
        props.data?.numberOfDays ||
        (props.data?.startDate && props.data?.endDate
          ?
          (new Date(props.data.endDate) - new Date(props.data.startDate)) /
          (1000 * 60 * 60 * 24)
          : ""),
      leaveTypeId: props.data?.leaveTypeId || "",
      companyId: props.data?.companyId || companyId,
      // when editing we fetch the relieverIds separately; if we haven't
      // loaded them yet fall back to whatever (if any) value is on the row
      // object.
      userId: existingRelievers.length > 0 ? existingRelievers : props.data?.userId || [],
      autoAccessCase: props.data?.autoAccessCase || false,
      autoAccessDocuments: props.data?.autoAccessDocuments || false,
      supportingDocument: null,
      applicationMode:
        props.isEditing &&
          props.data?.employeeId !== loggedInEmployeeId
          ? "team"
          : "self",
      employeeId:
        props.isEditing &&
          props.data?.employeeId !== loggedInEmployeeId
          ? props.data.employeeId
          : "",
    };
  }, [props.data, existingRelievers, companyId, loggedInEmployeeId]);


  /* =========================================================
     FETCH INITIAL DATA
  ========================================================= */
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Use allSettled so one failing query doesn't break the others
        const [leaveTypeResult, employeeResult, gendersResult, policyResult, holidayResult] =
          await Promise.allSettled([
            graphqlQuery(allLeaveTypes, setupManagementUrl.uri),
            graphqlQuery(AllEmployeesQuery, base_url),
            graphqlQuery(allGenders, setupManagementUrl.uri),
            graphqlQuery(allPoliciesView, base_url),
            graphqlQuery(companyCalendarSetups, base_url),
          ]);

        const leaveTypeData = leaveTypeResult.status === "fulfilled" ? leaveTypeResult.value : null;
        const employeeData = employeeResult.status === "fulfilled" ? employeeResult.value : null;
        const genders = gendersResult.status === "fulfilled" ? gendersResult.value : null;
        const policyData = policyResult.status === "fulfilled" ? policyResult.value : null;
        const holidayData = holidayResult.status === "fulfilled" ? holidayResult.value : null;

        if (leaveTypeData) setLeaveTypes(leaveTypeData);
        if (employeeData) setEmployees(employeeData);
        if (policyData) {
          console.log("Leave policies loaded:", policyData.length, policyData);
          setPolicies(policyData);
        } else {
          console.warn("Leave policies failed to load:", policyResult.reason);
        }
        if (holidayData) {
          console.log("Holidays loaded:", holidayData.length, holidayData);
          setHolidays(holidayData);
        } else {
          console.warn("Holidays failed to load:", holidayResult.reason);
        }

        if (genders) {
          const female = genders.find((g) =>
            g.gender?.toLowerCase().includes("female")
          );
          if (female) setFemaleGenderId(female.id);
        }

        // Determine logged-in employee gender
        if (employeeData) {
          const me = employeeData.find(
            (e) => e.id === loggedInEmployeeId
          );
          if (me) setSelectedEmployeeGenderId(me.genderId);
        }

        if (rolegroupId === 1 || rolegroupId === 2) {
          setShowEmployeeDropDown(true);
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Debug: monitor policies state
  useEffect(() => {
    console.log("[DEBUG] policies state changed:", policies.length, policies);
  }, [policies]);

  useEffect(() => {
    if (
      props.isEditing &&
      props.data &&
      employees.length > 0 &&
      leaveTypes.length > 0
    ) {
      // Determine application mode
      const isTeam =
        props.data.employeeId !== loggedInEmployeeId &&
        (rolegroupId === 1 || rolegroupId === 2);

      // Find employee
      const employee = employees.find(
        (e) => e.id === props.data.employeeId
      );

      // Set gender correctly
      if (employee) {
        setSelectedEmployeeGenderId(employee.genderId);
        setExcludeFromRelievers(employee.id);
      }

      // Set selected leave type
      setSelectedLeaveTypeId(props.data.leaveTypeId);

    }
  }, [
    props.isEditing,
    props.data,
    employees,
    leaveTypes,
  ]);

  // when we open the form to edit an existing application, load the
  // relievers list from the server so we can pre‑populate the
  // multi‑select field.  this mirrors the logic used in the
  // standalone reliever view/dialog.
  useEffect(() => {
    async function loadRelievers() {
      if (!props.isEditing || !props.data?.id) return;
      try {
        const result = await fetchDataEngine(
          "relieversByApplicationId",
          "reliever",
          { leaveApplicationId: Number(props.data.id) },
          ["relieverId"],
          base_url
        );
        if (result) {
          const ids = result.map((r) => r.relieverId).filter(Boolean);
          setExistingRelievers(ids);
        }
      } catch (err) {
        console.error("Failed to load relievers for editing", err);
      }
    }

    loadRelievers();
  }, [props.isEditing, props.data]);


  /* =========================================================
     LEAVE TYPE OPTIONS (Disable maternity if male)
  ========================================================= */
  const LeaveTypeOptions = useMemo(() => {
    return leaveTypes.map((type) => {
      const isMaternity =
        type.type?.toLowerCase().includes("maternity");

      const disabled =
        isMaternity &&
        selectedEmployeeGenderId &&
        femaleGenderId &&
        selectedEmployeeGenderId !== femaleGenderId;

      return {
        value: type.id,
        label: type.type,
        disabled, // 🔥 This greys it out
      };
    });
  }, [leaveTypes, selectedEmployeeGenderId, femaleGenderId]);

  const isMaternitySelected = selectedLeaveTypeId
    ? leaveTypes.find((t) => t.id === selectedLeaveTypeId)?.type
      ?.toLowerCase()
      .includes("maternity")
    : false;

  /* =========================================================
     FORM FIELDS
  ========================================================= */
  const fields = useMemo(() => [
    showEmployeeDropDown && {
      name: "applicationMode",
      label: "Apply For",
      type: "select",
      options: [
        { value: "self", label: "Apply for Self" },
        { value: "team", label: "Apply for Team Member" },
      ],
      required: true,
      gridColumn: "span 2",
      onChange: (value, values, setFieldValue) => {
        // When changing application mode, clear fields
        setFieldValue("startDate", "");
        setFieldValue("endDate", "");
        setFieldValue("numberOfDays", "");
        setFieldValue("leaveTypeId", "");
        setFieldValue("userId", []);

        if (value === "self") {
          const me = employees.find(
            (e) => e.id === loggedInEmployeeId
          );
          setSelectedEmployeeGenderId(me?.genderId);
          setExcludeFromRelievers(loggedInEmployeeId);
          setFieldValue("employeeId", "");
        } else {
          setExcludeFromRelievers(null);
          setSelectedEmployeeGenderId(null);
        }
      },
    },

    showEmployeeDropDown && {
      name: "employeeId",
      label: "Employee",
      type: "select",
      options: employees.map((emp) => ({
        value: emp.id,
        label: `${emp.firstName} ${emp.lastName} - ${emp.emailAddress}`,
      })),
      required: true,
      gridColumn: "span 2",
      visibleCondition: (values) => values.applicationMode === "team",
      onChange: (value) => {
        const emp = employees.find((e) => e.id === value);
        setSelectedEmployeeGenderId(emp?.genderId || null);
        setExcludeFromRelievers(value);
      },
    },

    {
      name: "leaveTypeId",
      label: "Leave Type",
      type: "select",
      options: LeaveTypeOptions,
      required: true,
      gridColumn: "span 2",
      onChange: (value, values, setFieldValue) => {
        setSelectedLeaveTypeId(value);

        // When changing leave type, clear date fields and relievers
        setFieldValue("startDate", "");
        setFieldValue("endDate", "");
        setFieldValue("numberOfDays", "");
        setFieldValue("userId", []);

        const selected = leaveTypes.find((t) => t.id === value);
        if (
          selected &&
          selected.type.toLowerCase().includes("maternity") &&
          selectedEmployeeGenderId !== femaleGenderId
        ) {
          Swal.fire({
            icon: "error",
            title: "Not Allowed",
            text: "You are not eligible to apply for a maternity leave.",
          });
          setFieldValue("leaveTypeId", "");
          setSelectedLeaveTypeId("");
          return;
        }

        // Auto-populate numberOfDays from the leave policy's maxDays
        const policyMatch = findPolicy(value);

        if (policyMatch && policyMatch.maxDays != null) {
          setFieldValue("numberOfDays", policyMatch.maxDays);
          // If startDate already set, auto-compute endDate too
          if (values.startDate) {
            const isAnnualLeave = selected?.type?.toLowerCase().includes("annual");
            let endDate;
            if (isAnnualLeave) {
              endDate = calculateEndDateByBusinessDays(values.startDate, parseInt(policyMatch.maxDays, 10));
            } else {
              const start = new Date(values.startDate);
              const end = new Date(start);
              end.setDate(end.getDate() + parseInt(policyMatch.maxDays, 10));
              endDate = end.toISOString().split("T")[0];
            }
            setFieldValue("endDate", endDate);
          }
        } else {
          setFieldValue("numberOfDays", "");
          setFieldValue("endDate", "");
        }
      },
    },

    {
      name: "startDate",
      label: "Start Date",
      type: "date",
      required: true,
      gridColumn: "span 2",
      onChange: (value, values, setFieldValue) => {
        if (value && values.numberOfDays) {
          const policyMatch = findPolicy(values.leaveTypeId);
          const maxDaysAllowed = policyMatch?.maxDays != null ? parseInt(policyMatch.maxDays, 10) : null;

          const requested = parseInt(values.numberOfDays, 10);
          if (maxDaysAllowed != null && requested > maxDaysAllowed) {
            Swal.fire({
              icon: "error",
              title: "Too many days",
              text: `The number of days cannot exceed ${maxDaysAllowed}.`,
            });
            setFieldValue("numberOfDays", maxDaysAllowed);
          }
          const selectedLeaveType = leaveTypes.find((t) => t.id === values.leaveTypeId);
          const isAnnualLeave = selectedLeaveType?.type?.toLowerCase().includes("annual");

          if (isAnnualLeave) {
            const endDate = calculateEndDateByBusinessDays(value, parseInt(values.numberOfDays, 10));
            setFieldValue("endDate", endDate);
          } else {
            const start = new Date(value);
            const end = new Date(start);
            end.setDate(end.getDate() + parseInt(values.numberOfDays, 10));
            setFieldValue("endDate", end.toISOString().split("T")[0]);
          }
        }
      },
    },

    {
      name: "numberOfDays",
      label: "Number of Leave Days",
      type: "number",
      required: false,
      gridColumn: "span 2",
      onChange: (value, values, setFieldValue) => {
        if (value && values.startDate) {
          const policyMatch = findPolicy(values.leaveTypeId);
          const maxDaysAllowed = policyMatch?.maxDays != null ? parseInt(policyMatch.maxDays, 10) : null;

          const requested = parseInt(value, 10);
          if (maxDaysAllowed != null && requested > maxDaysAllowed) {
            Swal.fire({
              icon: "error",
              title: "Too many days",
              text: `The number of days cannot exceed ${maxDaysAllowed}.`,
            });
            setFieldValue("numberOfDays", maxDaysAllowed);
          }

          const selectedLeaveType = leaveTypes.find((t) => t.id === values.leaveTypeId);
          const isAnnualLeave = selectedLeaveType?.type?.toLowerCase().includes("annual");

          if (isAnnualLeave) {
            const endDate = calculateEndDateByBusinessDays(values.startDate, parseInt(value, 10));
            setFieldValue("endDate", endDate);
          } else {
            const start = new Date(values.startDate);
            const end = new Date(start);
            end.setDate(end.getDate() + parseInt(value, 10));
            setFieldValue("endDate", end.toISOString().split("T")[0]);
          }
        } else if (!value) {
          setFieldValue("endDate", "");
        }
      },
    },

    {
      name: "endDate",
      label: "End Date",
      type: "date",
      required: true,
      gridColumn: "span 2",
      onChange: (value, values, setFieldValue) => {
        // recalc days when the end date changes
        if (value && values.startDate) {
          const diff =
            (new Date(value) - new Date(values.startDate)) /
            (1000 * 60 * 60 * 24);
          let days = diff >= 0 ? diff : "";
          // enforce policy max
          const policyMatch = findPolicy(values.leaveTypeId);
          const maxDaysAllowed = policyMatch?.maxDays != null ? parseInt(policyMatch.maxDays, 10) : null;
          if (maxDaysAllowed != null && days > maxDaysAllowed) {
            Swal.fire({
              icon: "error",
              title: "Too many days",
              text: `The selected period exceeds the maximum of ${maxDaysAllowed} days for this leave type.`,
            });
            days = maxDaysAllowed;
            const start = new Date(values.startDate);
            const end = new Date(start);
            end.setDate(end.getDate() + maxDaysAllowed);
            setFieldValue("endDate", end.toISOString().split("T")[0]);
          }
          setFieldValue("numberOfDays", days);
        }
      },
    },

    {
      name: "userId",
      label: "Reliever(s)",
      type: "multiselect",
      options: employees
        .filter((emp) => emp.id !== excludeFromRelievers)
        .map((emp) => ({
          value: emp.id,
          label: `${emp.firstName} ${emp.lastName} - ${emp.emailAddress}`,
        })),
      required: true,
      gridColumn: "span 2",
    },

    {
      name: "supportingDocument",
      label: "Supporting Document",
      type: "file",
      required: false,
      gridColumn: "span 2",
    },

    // Filter out the date fields from leaveApplicationFields since we've defined them above
    ...leaveApplicationFields.filter(
      (f) => !["startDate", "numberOfDays", "endDate"].includes(f.name)
    ),
  ].filter(Boolean), [
    showEmployeeDropDown,
    employees,
    leaveTypes,
    LeaveTypeOptions,
    selectedEmployeeGenderId,
    femaleGenderId,
    excludeFromRelievers,
    leaveApplicationFields,
    policies,
    holidays,
  ]);

  /* =========================================================
     STEPS
  ========================================================= */
  const steps = [
    {
      stepLabel: "Leave Type & Period",
      fields: [
        "applicationMode",
        "employeeId",
        "leaveTypeId",
        "startDate",
        "numberOfDays",
        "endDate",
      ],
    },
    {
      stepLabel: "Reliever Assignment",
      fields: ["userId"],
    },
    {
      stepLabel: "Access Settings",
      fields: ["supportingDocument", "autoAccessDocuments", "autoAccessCase"],
    },
  ];

  /* =========================================================
     SUBMIT
  ========================================================= */
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // If we're editing, don't submit if nothing changed.
      if (props.isEditing) {
        const sortIds = (arr) => (Array.isArray(arr) ? arr.map((x) => Number(x)).sort((a, b) => a - b) : []);
        const normalize = (obj) => ({
          startDate: formatDate(obj.startDate) || "",
          endDate: formatDate(obj.endDate) || "",
          leaveTypeId: obj.leaveTypeId || "",
          employeeId: obj.employeeId || "",
          userId: sortIds(obj.userId || []),
          autoAccessCase: !!obj.autoAccessCase,
          autoAccessDocuments: !!obj.autoAccessDocuments,
          numberOfDays: obj.numberOfDays || "",
          applicationMode: obj.applicationMode || "",
        });

        const normalizedValues = normalize(values);
        const normalizedInitial = normalize(initialValues);

        if (JSON.stringify(normalizedValues) === JSON.stringify(normalizedInitial)) {
          Swal.fire({
            icon: "info",
            title: "No changes",
            text: "You have not made any changes",
          });
          setSubmitting(false);
          return;
        }
      }

      // final safety check: make sure days do not exceed policy
      const selectedId = parseInt(values.leaveTypeId, 10);
      const policyMatch =
        policies.find(
          (p) => parseInt(p.leaveTypeId, 10) === selectedId &&
            (p.gender == null || p.gender === "" || p.gender === "-")
        ) ||
        policies.find((p) => parseInt(p.leaveTypeId, 10) === selectedId);
      const maxDaysAllowed = policyMatch?.maxDays != null ? parseInt(policyMatch.maxDays, 10) : null;
      const requestedDays = parseInt(values.numberOfDays, 10);
      if (maxDaysAllowed != null && requestedDays > maxDaysAllowed) {
        Swal.fire({
          icon: "error",
          title: "Too many days",
          text: `You cannot apply for more than ${maxDaysAllowed} days of this leave type.`,
        });
        setSubmitting(false);
        return;
      }
      let employeeId;

      const isApplyingForTeam =
        (rolegroupId === 1 || rolegroupId === 2) &&
        values.applicationMode === "team";

      if (isApplyingForTeam) {
        employeeId = values.employeeId;
      } else {
        employeeId = loggedInEmployeeId;
      }

      const relievers = values.userId.map((id) => ({
        employeeId: id,
        autoAccessDocuments: values.autoAccessDocuments,
        autoAccessCase: values.autoAccessCase,
      }));

      // Calculate resumption date = next WORKING DAY after endDate.
      // Uses local date parts to avoid UTC timezone shifting.
      // Skips Saturday (6) and Sunday (0) to match backend behaviour
      // (e.g. leave ending Friday → resumes Monday).
      const computeResumptionDate = (endDateStr) => {
        if (!endDateStr) return null;
        const [yyyy, mm, dd] = endDateStr.split("-").map(Number);
        const d = new Date(yyyy, mm - 1, dd); // local midnight
        // advance at least one day, then keep skipping weekends
        do {
          d.setDate(d.getDate() + 1);
        } while (d.getDay() === 0 || d.getDay() === 6); // 0=Sun, 6=Sat
        const ry = d.getFullYear();
        const rm = String(d.getMonth() + 1).padStart(2, "0");
        const rd = String(d.getDate()).padStart(2, "0");
        return `${ry}-${rm}-${rd}`;
      };

      const resumptionDate = computeResumptionDate(values.endDate);

      const mutationData = {
        leave: {
          startDate: values.startDate,
          endDate: values.endDate,
          resumptionDate,
          id: props.isEditing ? props.data.id : null,
          leaveTypeId: values.leaveTypeId,
          companyId,
          userId,
          employeeId,
        },
        relievers,
      };

      const response = await MutationWithArrayInput(
        props.isEditing
          ? "updateLeaveApplication"
          : "createLeaveApplication",
        mutationData,
        null,
        base_url,
        ["statusCode", "errorMessage", "applicationId"]
      );

      const result = props.isEditing
        ? response.updateLeaveApplication
        : response.createLeaveApplication;

      if (!result) {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: "Unexpected server response. Please try again.",
        });
        setSubmitting(false);
        return;
      }

      if (result.statusCode === 200) {
        let uploadFailureMessage = null;

        // Upload supporting document if one was selected
        if (values.supportingDocument) {
          try {
            const leaveAppId = props.isEditing
              ? props.data.id
              : (result.applicationId || props.data?.id);

            if (!leaveAppId) {
              uploadFailureMessage =
                "Leave was saved but document upload was skipped because the leave application ID was not returned.";
            } else {
              await uploadDocuments(
                "/upload/leave-application",
                {
                  leave_application_id: leaveAppId,
                  employee_id: employeeId,
                },
                values.supportingDocument
              );
              console.log("Leave document uploaded successfully");
            }
          } catch (uploadError) {
            console.error("Error uploading leave document:", uploadError);
            uploadFailureMessage =
              "Leave was saved but document upload failed. Please retry from View Documents.";
          }
        }

        props.onClose();
        props.onAction();
        Swal.fire({
          icon: uploadFailureMessage ? "warning" : "success",
          title: uploadFailureMessage ? "Partially Completed" : "Success!",
          text: uploadFailureMessage || `Leave application ${
            props.isEditing ? "updated" : "created"
          } successfully.`,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: result.errorMessage || "An error occurred while processing your request.",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request.",
      });
    }

    setSubmitting(false);
  };

  return loading ? (
    <CircularProgress />
  ) : (
    <DynamicForm
      enableReinitialize
      initialValues={initialValues}
      fields={fields}
      steps={steps}
      onSubmit={handleSubmit}
      title={
        props.isEditing
          ? "Update Leave Application"
          : "Create Leave Application"
      }
      subtitle="Fill out the form below to apply for leave."
    />
  );
};

export default LeaveApplicationForm;
